/**
 * Public portfolio routes.
 *
 * A teacher owns ONE subdomain and may publish SEVERAL portfolios under it —
 * one per subject:
 *
 *   mahmoud-tulaimat.neoprof.fr            → the default portfolio (subject '')
 *   mahmoud-tulaimat.neoprof.fr/physique   → the physics portfolio
 *
 * The teacher slug is `firstname-lastname`; the subject slug is the subject
 * label run through the same normaliser. Both are lowercased, accents folded,
 * every other character collapsed to a single dash.
 *
 * These functions produce the values stored in `portfolio.teacher_slug` and
 * `portfolio.subject_slug` when the backend lands — keep them the single
 * source of truth.
 */

// Hoisted: never rebuild a RegExp inside a function.
const COMBINING_MARKS = /\p{Diacritic}/gu;
const NON_SLUG_CHARS = /[^a-z0-9]+/g;
const EDGE_DASHES = /^-+|-+$/g;
const HOST_PORT = /:\d+$/;
const PATH_ROUTE = /^\/p\/([^/?#]+)(?:\/([^/?#]+))?/i;
const FIRST_SEGMENT = /^\/([^/?#]+)/;

/** Characters Unicode NFD cannot decompose — mapped by hand. */
const TRANSLITERATIONS = new Map([
  ['æ', 'ae'], ['œ', 'oe'], ['ø', 'o'], ['å', 'a'], ['ð', 'd'], ['đ', 'd'],
  ['þ', 'th'], ['ß', 'ss'], ['ł', 'l'], ['ı', 'i'], ['ħ', 'h'], ['ŋ', 'n'],
]);

/** Subdomains that can never belong to a teacher. */
const RESERVED_SUBDOMAINS = new Set([
  'www', 'api', 'app', 'admin', 'staging', 'preview', 'dev', 'test', 'mail', 'cdn',
  'formations', 'courses',
  'calcul', 'bac', 'brevet', 'annales', 'scratch', 'exam', 'learn',
]);

/** Root domain portfolios are served from. */
export const PORTFOLIO_ROOT_DOMAIN = 'neoprof.fr';

/** Path prefix used in development and on Vercel preview URLs, where subdomains do not exist. */
export const PORTFOLIO_PATH_PREFIX = '/p';

/**
 * Normalise any string into a URL-safe slug fragment.
 * @param {string} value
 * @returns {string} empty string when nothing survives normalisation
 */
export function slugify(value) {
  if (!value) return '';

  let lowered = String(value).toLowerCase();
  for (const [char, replacement] of TRANSLITERATIONS) {
    if (lowered.includes(char)) lowered = lowered.split(char).join(replacement);
  }

  return lowered
    .normalize('NFD')
    .replace(COMBINING_MARKS, '')
    .replace(NON_SLUG_CHARS, '-')
    .replace(EDGE_DASHES, '');
}

/**
 * Build a teacher's public slug — their subdomain, shared by all their portfolios.
 * @param {string} firstName
 * @param {string} lastName
 * @returns {string}
 */
export function buildTeacherSlug(firstName, lastName) {
  return [slugify(firstName), slugify(lastName)].filter(Boolean).join('-');
}

/**
 * Build a subject slug from its label. An empty result means "the teacher's
 * default portfolio", served at the bare subdomain.
 * @param {string} subjectLabel
 * @returns {string}
 */
export function buildSubjectSlug(subjectLabel) {
  return slugify(subjectLabel);
}

/**
 * DOM id of one result card, so the hero can link a student's name straight to
 * their story. Student alone is not unique — one teacher has two Vincents — so
 * the scope goes in too, exactly as it does in the React key.
 *
 * @param {{ student?: string, scope?: string }} result
 * @returns {string}
 */
export function buildResultAnchorId({ student = '', scope = '' } = {}) {
  return ['resultat', slugify(student), slugify(scope)].filter(Boolean).join('-');
}

/**
 * Identity of one portfolio: `<teacher>` for the default, `<teacher>/<subject>`
 * otherwise. Used as the registry key today and as the `(teacher_slug,
 * subject_slug)` unique pair in the future table.
 *
 * @param {{ teacher?: string, subject?: string }} route
 * @returns {string} empty string when there is no teacher to key on
 */
export function portfolioKey({ teacher = '', subject = '' } = {}) {
  if (!teacher) return '';
  return subject ? `${teacher}/${subject}` : teacher;
}

/**
 * Absolute public URL of a portfolio — always the subdomain form, which is canonical.
 * @param {{ teacher?: string, subject?: string }} route
 * @returns {string}
 */
export function buildPortfolioUrl({ teacher = '', subject = '' } = {}) {
  const origin = `https://${teacher}.${PORTFOLIO_ROOT_DOMAIN}`;
  return subject ? `${origin}/${subject}` : origin;
}

/**
 * In-page path to a portfolio, in whichever form the current host understands.
 * On the canonical subdomain the teacher is already the host, so only the
 * subject remains; everywhere else the `/p/` fallback carries both.
 *
 * @param {{ teacher?: string, subject?: string }} route
 * @param {{ canonical?: boolean }} options
 * @returns {string}
 */
export function buildPortfolioPath({ teacher = '', subject = '' } = {}, { canonical = false } = {}) {
  if (canonical) return subject ? `/${subject}` : '/';
  return subject
    ? `${PORTFOLIO_PATH_PREFIX}/${teacher}/${subject}`
    : `${PORTFOLIO_PATH_PREFIX}/${teacher}`;
}

/**
 * Resolve which portfolio the current request is asking for.
 *
 * Production reads the teacher from the subdomain and the subject from the
 * first path segment. Localhost and Vercel previews have no subdomain, so they
 * fall back to `/p/:teacher/:subject?` — one helper, both environments, no
 * hosts-file editing.
 *
 * Returns `null` — never a truthy empty object — for every non-portfolio route,
 * because App.js uses the result to decide whether to render the app at all.
 *
 * @param {{ hostname?: string, pathname?: string }} location
 * @returns {{ teacher: string, subject: string }|null}
 */
export function resolvePortfolioRoute({ hostname = '', pathname = '' } = {}) {
  const host = hostname.replace(HOST_PORT, '').toLowerCase();
  const labels = host.split('.');

  // <teacher>.neoprof.fr — at least three labels, root domain at the tail.
  if (labels.length >= 3 && labels.slice(-2).join('.') === PORTFOLIO_ROOT_DOMAIN) {
    const teacher = labels[0];
    if (teacher && !RESERVED_SUBDOMAINS.has(teacher)) {
      const segment = FIRST_SEGMENT.exec(pathname);
      return {
        teacher,
        subject: segment ? slugify(decodeURIComponent(segment[1])) : '',
      };
    }
  }

  const fromPath = PATH_ROUTE.exec(pathname);
  if (fromPath) {
    return {
      teacher: slugify(decodeURIComponent(fromPath[1])),
      subject: fromPath[2] ? slugify(decodeURIComponent(fromPath[2])) : '',
    };
  }

  return null;
}

/**
 * True when the portfolio is being served from its canonical host, so the page
 * can emit `noindex` on the `/p/:teacher` dev fallback.
 * @param {{ hostname?: string }} location
 * @returns {boolean}
 */
export function isCanonicalPortfolioHost({ hostname = '' } = {}) {
  const host = hostname.replace(HOST_PORT, '').toLowerCase();
  return host.endsWith(`.${PORTFOLIO_ROOT_DOMAIN}`);
}
